import type { ReactNode } from "react";
import type { CustomFieldFilter } from "@forgepoint/shared";
import {
  FilterBar,
  type FilterDefinition,
} from "@/components/filters/FilterBar";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { UserSelector } from "@/components/UserSelector";
import { OpportunitySelector } from "@/components/OpportunitySelector";
import { customFieldFilterDefinition } from "@/components/filters/customFieldFilterDefinition";
import {
  customFieldId,
  parseCustomFieldId,
  withCustomFieldSelection,
  type FilterableCustomField,
} from "@/lib/customFieldFilters";
import { useUserDisplayNames } from "@/store/teamsStore";
import {
  COMPLETED_VISIBILITY_OPTIONS,
  INBOX_FILTER_TYPES,
  INBOX_TASK_TYPE_OPTIONS,
  type InboxFilterKey,
  type InboxTaskType,
} from "@/pages/inboxFilterTypes";

export interface InboxFilterBarProps {
  filterUserId: string | null;
  filterUserName: string | null;
  filterOpportunityId: string | null;
  filterOpportunityName: string | null;
  taskType: InboxTaskType | null;
  showCompleted: boolean;
  filterCustomFields: CustomFieldFilter;
  customFields: FilterableCustomField[];
  onFilterUserIdChange: (userId: string | null) => void;
  onFilterOpportunityIdChange: (opportunityId: string | null) => void;
  onTaskTypeChange: (taskType: InboxTaskType | null) => void;
  onShowCompletedChange: (showCompleted: boolean) => void;
  onFilterCustomFieldsChange: (customFields: CustomFieldFilter) => void;
  onClearAll: () => void;
  canFilterByUser: boolean;
  canFilterByType: boolean;
  canFilterCompleted: boolean;
}

/**
 * The task inbox's filter bar. Wraps the generic {@link FilterBar} with the
 * inbox's filter types (Assignee, Opportunity, Type, Completed, and one per
 * filterable opportunity custom field), supplying each one's value summary and
 * editing dropdown.
 * @param filterUserId - The assignee currently filtered on, or null
 * @param filterUserName - That assignee's display name, for the chip summary
 * @param filterOpportunityId - The opportunity currently filtered on, or null
 * @param filterOpportunityName - That opportunity's name, for the chip summary
 * @param taskType - The task type currently filtered on, or null for all
 * @param showCompleted - Whether completed tasks are listed
 * @param filterCustomFields - The active opportunity custom field selections
 * @param customFields - The opportunity custom fields to offer as filters
 * @param onFilterUserIdChange - Called with the next assignee selection
 * @param onFilterOpportunityIdChange - Called with the next opportunity selection
 * @param onTaskTypeChange - Called with the next task type selection
 * @param onShowCompletedChange - Called with the next completed visibility
 * @param onFilterCustomFieldsChange - Called with the next custom field selections
 * @param onClearAll - Called to reset every selection at once
 * @param canFilterByUser - Whether the signed-in user may filter by assignee
 * @param canFilterByType - Whether the signed-in user may filter by task type
 * @param canFilterCompleted - Whether the current inbox scope may filter by completion
 * @returns The inbox filter bar
 */
export function InboxFilterBar({
  filterUserId,
  filterUserName,
  filterOpportunityId,
  filterOpportunityName,
  taskType,
  showCompleted,
  filterCustomFields,
  customFields,
  onFilterUserIdChange,
  onFilterOpportunityIdChange,
  onTaskTypeChange,
  onShowCompletedChange,
  onFilterCustomFieldsChange,
  onClearAll,
  canFilterByUser,
  canFilterByType,
  canFilterCompleted,
}: InboxFilterBarProps) {
  const userNames = useUserDisplayNames();

  const summaryFor = (key: InboxFilterKey): string | null => {
    if (key === "assignee") {
      if (!filterUserId) {
        return null;
      }
      return filterUserName ?? "1 selected";
    }
    if (key === "opportunity") {
      if (!filterOpportunityId) {
        return null;
      }
      return filterOpportunityName ?? "1 selected";
    }
    if (key === "type") {
      return (
        INBOX_TASK_TYPE_OPTIONS.find((option) => option.value === taskType)
          ?.label ?? null
      );
    }
    return showCompleted ? "Shown" : null;
  };

  const contentFor = (key: InboxFilterKey): ReactNode => {
    if (key === "assignee") {
      return (
        <div className="p-3">
          <UserSelector
            currentlySelectedId={filterUserId}
            onSelectId={onFilterUserIdChange}
            placeholder="Anyone"
            className="w-full"
          />
        </div>
      );
    }
    if (key === "opportunity") {
      return (
        <div className="p-3">
          <OpportunitySelector
            currentlySelectedId={filterOpportunityId}
            onSelectId={onFilterOpportunityIdChange}
            placeholder="Any opportunity"
            className="w-full"
          />
        </div>
      );
    }
    if (key === "type") {
      return (
        <RadioGroup
          className="p-3"
          value={taskType ?? ""}
          onValueChange={(value) =>
            onTaskTypeChange(
              INBOX_TASK_TYPE_OPTIONS.find((option) => option.value === value)
                ?.value ?? null,
            )
          }
        >
          {INBOX_TASK_TYPE_OPTIONS.map((option) => (
            <div key={option.value} className="flex items-center gap-2">
              <RadioGroupItem
                value={option.value}
                id={`inbox-type-${option.value}`}
              />
              <Label
                htmlFor={`inbox-type-${option.value}`}
                className="cursor-pointer font-normal"
              >
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      );
    }
    return (
      <RadioGroup
        className="p-3"
        value={showCompleted ? "show" : "hide"}
        onValueChange={(value) => onShowCompletedChange(value === "show")}
      >
        {COMPLETED_VISIBILITY_OPTIONS.map((option) => (
          <div key={option.value} className="flex items-center gap-2">
            <RadioGroupItem
              value={option.value}
              id={`inbox-completed-${option.value}`}
            />
            <Label
              htmlFor={`inbox-completed-${option.value}`}
              className="cursor-pointer font-normal"
            >
              {option.label}
            </Label>
          </div>
        ))}
      </RadioGroup>
    );
  };

  const setCustomFieldSelection = (fieldName: string, next: string[]) => {
    onFilterCustomFieldsChange(
      withCustomFieldSelection(filterCustomFields, fieldName, next) ?? {},
    );
  };

  const filters: FilterDefinition[] = [
    ...INBOX_FILTER_TYPES.filter((type) => {
      if (type.key === "assignee") {
        return canFilterByUser;
      }
      if (type.key === "type") {
        return canFilterByType;
      }
      if (type.key === "completed") {
        return canFilterCompleted;
      }
      return true;
    }).map((type) => ({
      key: type.key,
      label: type.label,
      icon: type.icon,
      summary: summaryFor(type.key),
      content: contentFor(type.key),
    })),
    ...customFields.map((field) =>
      customFieldFilterDefinition({
        field,
        selected: filterCustomFields[field.name] ?? [],
        userName: (id) => userNames.get(id) ?? null,
        onChange: (next) => setCustomFieldSelection(field.name, next),
      }),
    ),
  ];

  const removeFilter = (key: string) => {
    const fieldName = parseCustomFieldId(key);
    if (fieldName !== null) {
      setCustomFieldSelection(fieldName, []);
      return;
    }
    if (key === "assignee") {
      onFilterUserIdChange(null);
      return;
    }
    if (key === "opportunity") {
      onFilterOpportunityIdChange(null);
      return;
    }
    if (key === "type") {
      onTaskTypeChange(null);
      return;
    }
    if (key === "completed") {
      onShowCompletedChange(false);
    }
  };

  const initialActiveKeys: InboxFilterKey[] = [];
  if (canFilterByUser && filterUserId) {
    initialActiveKeys.push("assignee");
  }
  if (filterOpportunityId) {
    initialActiveKeys.push("opportunity");
  }
  if (canFilterByType && taskType) {
    initialActiveKeys.push("type");
  }
  if (canFilterCompleted && showCompleted) {
    initialActiveKeys.push("completed");
  }
  initialActiveKeys.push(...Object.keys(filterCustomFields).map(customFieldId));

  return (
    <FilterBar
      filters={filters}
      initialActiveKeys={initialActiveKeys}
      onRemove={removeFilter}
      onClearAll={onClearAll}
    />
  );
}
